import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Componente para ajustar los límites del mapa automáticamente
 * @param {Array} taskMarkers - Array de marcadores con lat/lng
 * @param {Array} fitPoints - Array opcional de puntos [lat, lng] o {lat, lng} para encuadrar
 */
function MapBounds({ taskMarkers, maxBounds, fitPoints = [] }) {
    const map = useMap();
    const hasAppliedInitialFit = useRef(false);

    useEffect(() => {
        if (maxBounds) {
            map.setMaxBounds(maxBounds);
            const minZoom = map.getBoundsZoom(maxBounds, true);
            map.setMinZoom(minZoom);
        }
    }, [maxBounds, map]);

    useEffect(() => {
        // El encuadre automatico es solo inicial. Los cambios de estado del
        // recorrido no deben pisar el zoom ni el paneo hechos por el usuario.
        if (hasAppliedInitialFit.current) {
            return;
        }

        if (fitPoints.length > 1) {
            const normalizedPoints = fitPoints.map((point) => (
                Array.isArray(point)
                    ? point
                    : [point.lat, point.lng]
            ));

            const minLat = Math.min(...normalizedPoints.map(([lat]) => lat));
            const maxLat = Math.max(...normalizedPoints.map(([lat]) => lat));
            const minLng = Math.min(...normalizedPoints.map(([, lng]) => lng));
            const maxLng = Math.max(...normalizedPoints.map(([, lng]) => lng));

            map.fitBounds([[minLat, minLng], [maxLat, maxLng]], {
                padding: [20, 20],
                maxZoom: 15
            });
            hasAppliedInitialFit.current = true;
            return;
        }

        if (taskMarkers.length > 1) {
            // Crear límites que incluyan todos los círculos (radio de 300m)
            const bounds = taskMarkers.map(marker => [
                [marker.lat - 0.003, marker.lng - 0.003], // Aproximadamente 300m al sur y oeste
                [marker.lat + 0.003, marker.lng + 0.003]  // Aproximadamente 300m al norte y este
            ]);

            // Expandir los límites para incluir todos los círculos
            const allBounds = bounds.flat();
            const minLat = Math.min(...allBounds.map(b => b[0]));
            const maxLat = Math.max(...allBounds.map(b => b[0]));
            const minLng = Math.min(...allBounds.map(b => b[1]));
            const maxLng = Math.max(...allBounds.map(b => b[1]));

            map.fitBounds([[minLat, minLng], [maxLat, maxLng]], {
                padding: [20, 20],
                maxZoom: 15
            });
            hasAppliedInitialFit.current = true;
        }
    }, [fitPoints, taskMarkers, map]);

    return null;
}

export default MapBounds;
